import { makeAutoObservable, runInAction } from 'mobx';
import { CONTENT_TYPE, METHOD } from '../constants/method';
import { callApi } from '../util/apiWrapper';
import { appInsights } from '../services/appInsights';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { DefaultErrorMessage } from '../constants/general';
import API from '../constants/api';

class eLearningStore {
  state = {
    loading: false,
    error: null,
    senior: null,
    scormCourses: [],
  };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.loadState();
  }

  get error() {
    return this.state.error;
  }

  clearError() {
    this.setStateField('error', null);
  }

  /**
   * Helper method to cleanly set the error state and log it
   * @param {string} errorMessage
   */
  _setError(errorMessage) {
    runInAction(() => {
      this.setStateField('error', errorMessage);
    });
  }

  /**
   * Helper function to merge state updates while saving to sessionStorage
   * @param {object} newState New state values
   */
  _saveStateWithMerge(newState) {
    this.state = { ...this.state, ...newState };
    this.saveState();
  }

  /**
   * Single state field modification with saving
   * @param {string} key State key to update
   * @param {any} value The value to set for the key
   */
  setStateField(key, value) {
    this._saveStateWithMerge({ [key]: value });
  }

  /**
   * Merges multiple state fields with saving
   * @param {object} newState Object containing new state fields
   */
  setState(newState) {
    this._saveStateWithMerge(newState);
  }

  resetState() {
    this._saveStateWithMerge({
      loading: false,
      error: null,
      senior: null,
      scormCourses: [],
    });
  }

  saveState() {
    sessionStorage.setItem('eLearningStore', JSON.stringify(this.state));
  }

  loadState(senior = null) {
    const savedState = JSON.parse(sessionStorage.getItem('eLearningStore'));
    if (savedState) {
      if (senior !== null && savedState.senior.userId === senior.userId) {
        savedState.senior = senior;
        this.state = savedState;
      } else if (senior === null) {
        this.state = savedState;
      } else {
        this.state.senior = senior;
        this.saveState();
      }
    } else if (senior !== null) {
      this.state.senior = senior;
      this.saveState();
    }
  }

  deleteStore() {
    this.resetState();
    sessionStorage.removeItem('eLearningStore');
  }

  /**
   * Retrieve scormFiles from the backend API
   * @param {object} authStore Store holding authentication details
   * @param {string} courseId courseId to fetch launch link for
   * @param {number} userId userId of the user to fetch launch link for
   * @param {number} eCourseId id of the e-course and this is unique value.
   */
  async getScormLaunchLink(authStore, courseId, userId = this.state.senior.userId, eCourseId = null) {
    this.setStateField('loading', true);
    try {
      const existingCourse = this.state.scormCourses.find((course) => course.course_id === courseId && course.e_course_id === eCourseId) || {};

      const data = await callApi(
        authStore,
        API.LAUNCH_SCORM,
        METHOD.POST,
        { user_id: userId, course_id: courseId, e_course_id: eCourseId },
        CONTENT_TYPE.APPLICATION_JSON
      );

      existingCourse.launch_url = data?.launch_url || null;
      existingCourse.course_id = courseId;
      existingCourse.e_course_id = eCourseId;
      existingCourse.registration_id = data?.registration_id || null;

      // replace the course in the state if it exists, otherwise add it
      const updatedCourses = this.state.scormCourses.filter((course) => course.course_id !== courseId || course.e_course_id !== eCourseId);
      updatedCourses.push(existingCourse);
      this._saveStateWithMerge({ loading: false, error: null, scormCourses: updatedCourses });

      return existingCourse.launch_url;
    } catch (error) {
      appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
      this._setError(DefaultErrorMessage('fetching the launch link'));
      this.setStateField('loading', false);
    }
  }

  /**
   * Update course status
   * @param {object} authStore Store holding authentication details
   * @param {string} courseId courseId. This is not unique
   * @param {number} userId userId of the user id
   * @param {number} eCourseId id of the e-course id and this is unique value.
   */
  async updateCourseStatus(authStore, courseId, userId = this.state.senior.userId, eCourseId) {
    this.setStateField('loading', true);
    try {
      const existingCourse = this.state.scormCourses.find((course) => course.course_id === courseId && course.e_course_id === eCourseId) || {
        course_id: courseId,
        e_course_id: eCourseId,
      };
      const data = await callApi(
        authStore,
        API.UPDATE_E_COURSE_STATUS,
        METHOD.PUT,
        {
          user_id: userId,
          e_course_id: eCourseId,
          course_id: courseId,
          registration_id: this.state.scormCourses.find((course) => course.course_id === courseId && course.e_course_id === eCourseId)
            ?.registration_id,
        },
        CONTENT_TYPE.APPLICATION_JSON
      );
      existingCourse.completed = data?.completed_ts || null;
      existingCourse.course_id = courseId;
      existingCourse.e_course_id = eCourseId;
      const updatedCourses = this.state.scormCourses.filter((course) => course.course_id !== courseId || course.e_course_id !== eCourseId);
      updatedCourses.push(existingCourse);
      this._saveStateWithMerge({ loading: false, error: null, scormCourses: updatedCourses });
      this.setStateField('loading', false);
      return !!existingCourse.completed;
    } catch (error) {
      appInsights.trackException({ error: error, severityLevel: SeverityLevel.Error });
      this._setError(DefaultErrorMessage('updating the scorm course'));
      this.setStateField('loading', false);
    }
  }
}

export default eLearningStore;
